/**
 * What a hosted agent is told about itself, in the owner's words.
 *
 * ONE BOX, BECAUSE IT IS ONE BRIEF - the old tab's first note, and still the argument: an
 * owner writes this the way they would brief a new receptionist, and separate fields for
 * "tone" and "rules" would be a second structure to learn and be fed back to the model as
 * one text anyway.
 *
 * THE DETAILS ARE COLOURED AS YOU TYPE. A detail the Worker fills in is purple; one it does
 * not know is red, with a strip underneath naming it, because the Worker refuses the save and
 * "bussiness_name" reaching the model verbatim on every call is the failure the refusal
 * exists for.
 *
 * A TOOL IS NAMED AS `@book_appointment`, and one no tool answers to is named in the same
 * strip - as a warning, not a refusal: the model reads it as words, and a tool renamed later
 * would otherwise make a brief saved yesterday unsaveable today.
 *
 * MARKDOWN IS SHOWN AS WHAT IT MEANS, Notion- and Obsidian-style: `**`, `## ` and the rest
 * are hidden except on the line being written, where they come back to be edited. The text
 * the model gets is unchanged. See `BriefEditor.tsx`, which is loaded on demand with the
 * editor it is built on; until it arrives the brief is shown as plain text in the same card.
 *
 * AN EMPTY BOX IS THE DEFAULT BRIEF, shown as the placeholder so an owner can read what their
 * agent runs on before they have written a word. The words wait for the page's one Save.
 */

import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import type { AgentPage, Tool } from "../../../api";
import { SettingCard } from "../../../ui";
import type { BriefTool } from "../BriefEditor";
import { segmentPlaceholders, toolMentions } from "../brief";
import { useDraft } from "../draft";

/** The Worker's `MAX_INSTRUCTIONS_CHARS`. */
const MAX_INSTRUCTIONS_CHARS = 20_000;

const BriefEditor = lazy(() => import("../BriefEditor"));

export function InstructionsPane({ block, tools }: { block: NonNullable<AgentPage["instructions"]>; tools: Tool[] }) {
  const { state, set } = useDraft();
  const text = state.draft.instructions.text;
  const known = block.variables.map((variable) => variable.key);
  const { unknown } = segmentPlaceholders(text, known);
  // The name as typed on Details, before Save; the rest are what a call starting now reads.
  const details = block.variables.map((variable) =>
    variable.key === "business_name" && state.draft.business.name.trim() ? { ...variable, value: state.draft.business.name.trim() } : variable,
  );
  // A switch flipped on Tools and not yet saved is what the next call will have.
  const briefTools: BriefTool[] = tools.map((tool) => ({
    name: tool.name,
    description: tool.description,
    availability: tool.availability,
    enabled: state.draft.tools[tool.id] ?? tool.enabled,
  }));
  const noTool = [...new Set(toolMentions(text, briefTools).filter((m) => m.state === "unknown").map((m) => `@${m.name}`))];
  // An unclosed `{{` swallows everything to the next `}}`; name it by its first line, briefly.
  const named = unknown.map((name) => {
    const line = name.split("\n")[0]!.trim();
    return line.length > 30 || line !== name.trim() ? `${line.slice(0, 30)}…` : line;
  });

  return (
    <SettingCard className="editor-shell">
      <Suspense
        fallback={
          <div aria-busy="true" className="min-h-[370px] px-5 pt-[70px] pb-4.5 text-base leading-[1.65] whitespace-pre-wrap text-ink-soft">
            {text}
          </div>
        }
      >
        <BriefEditor
          value={text}
          onChange={(next) => set("instructions", { text: next })}
          details={details}
          tools={briefTools}
          placeholderText={block.placeholder || "Leave empty to use our default brief."}
          maxLength={MAX_INSTRUCTIONS_CHARS}
        />
      </Suspense>
      {(unknown.length > 0 || noTool.length > 0) && (
        <div role="status" className="grid gap-1 rounded-b-lg border-t border-alert-line bg-alert-wash px-4 py-2.5 text-xs text-alert">
          {unknown.length > 0 && (
            <Warning>Not a detail we can fill in: {named.join(", ")}. Use Insert detail to add one.</Warning>
          )}
          {noTool.length > 0 && (
            <Warning>
              No tool called {noTool.join(", ")}, so the agent reads {noTool.length > 1 ? "them" : "it"} as plain words. Use Insert tool to name one.
            </Warning>
          )}
        </div>
      )}
    </SettingCard>
  );
}

function Warning({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span aria-hidden="true" className="size-1.5 flex-none rounded-full bg-alert" />
      <span>{children}</span>
    </div>
  );
}
